import { Box } from '@mui/material'
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { trackEvent } from '_services/posthog'

import Navigation from '@hapana/shared/web/components/shared/navigation/navigation.component'
import { LayoutSkeleton } from '_components/page-skeletons'
import { hasStoredCoreSession } from '_utils/stored-session.util'
import NavigationHeader from '_components/navigation-header/navigation-header.component'
import { useMe, useMeProfileImage } from '_hooks/me.hooks'
import { usePermissions } from '_stores/permissions.store'
import { BOTTOM_NAVIGATIONS, navItems, locationNavItems } from '_pages/layout/layout.constants'
import { SCHEDULE_PERMISSIONS } from '@hapana/shared/web/components/shared/access-policies/location-permissions.constants'
import { getAllPermissionKeys } from '@hapana/shared/web/components/shared/access-policies/location-permissions.utils'
import IconMapMarker from '@hapana/shared/web/styles/assets/svgs/map-marker-alt-light-green.svg'
import {
  SETTINGS_BOTTOM_NAVIGATIONS,
  settingsNavItems,
  SETTINGS_ROUTES,
} from '_pages/layout/settings-layout.constants'
import { BRAND_NAME } from '_components/breadcrumbs/breadcrumbs.constant'
import { useBreadcrumbs } from '@hapana/shared/web/components/shared/breadcrumbs/breadcrumbs.hooks'
import CoreBreadCrumbs from '_components/breadcrumbs/breadcrumbs.component'
import usePathSegment from '_hooks/use-path-segment.hooks'
import useBreadcrumbStore from '_stores/breadcrumb.store'

import { useAuth, useLoginAsSync } from '../../hooks/auth.hooks'
import { useImpersonationSession } from '_hooks/use-impersonation-session'
import { useSiteNavigation } from '_hooks/use-site-navigation'
import { useCoreEntitlements } from '_hooks/use-core-entitlements'
import useSiteStore from '_stores/site.store'
import useBannerStore from '_stores/banner.store'
import IdleSessionGuard from '_components/idle-session-guard/idle-session-guard.component'
import CheckInFloatingButton from '_components/check-in-widget/check-in-button.component'
import { useFeatureFlag, FeatureFlagKeys } from '_hooks/useFeatureFlag'
import { useBrandReportsAccess } from '_hooks/use-brand-reports-access.hooks'
import { useCapitalAccess } from '_pages/capital/use-capital.hooks'
import UnsavedChangesBanner from '@hapana/shared/web/components/shared/unsaved-changes-banner/unsaved-changes-banner.component'
import ImpersonationBanner from '_components/impersonation-banner/impersonation-banner.component'
import SandboxBanner from '_components/sandbox-banner/sandbox-banner.component'
import PaymentGatewayBanner from '_components/payment-gateway-banner/payment-gateway-banner.component'
import { usePaymentGatewayOnboardingStatus } from '_hooks/use-payment-gateway-onboarding-status.hooks'
import {
  SITE_DATE_FORMAT_KEY,
  SITE_TIME_FORMAT_KEY,
  SITE_NUMBER_FORMAT_KEY,
} from '@hapana/shared/web/utils/date-format-utils'
import { useGetSiteById } from '_hooks/sites.hooks'

import OnboardingWrapper from '_components/onboarding/onboarding-wrapper.component'
import HideOnboardingDialog from '_components/onboarding/hide-onboarding-dialog'
import { useOnboardingStore } from '_stores/onboarding.store'
import { useSupportAssistant } from '_components/support-assistant/support-assistant.hooks'
import { useTrialStatus } from '_hooks/use-trial-management.hooks'
import { useCorporateWithStore } from '_stores/corporate.store'
import IconClipboardCheck from '@hapana/shared/web/styles/assets/svgs/clipboard-check.svg'
import IconAssistant from '@hapana/shared/web/styles/assets/svgs/hapana-ai-icon.svg'

import { styles } from './layout.styles'
import { ROUTES_WITHOUT_BREADCRUMBS } from './layout.constants'

import { PROTECTED_ROUTES_PATHS, ROUTES } from '../../app.constants'
import { COVER_BOARD_PERMISSIONS } from '_pages/covers/covers.constants'
import { useKBarShortcuts } from '_components/kbar/use-kbar'

const NAV_OPEN_STORAGE_KEY = 'hapana_nav_open'

// Get initial nav state from localStorage
const getInitialNavState = () => {
  if (typeof window === 'undefined') {
    return false
  }
  const stored = localStorage.getItem(NAV_OPEN_STORAGE_KEY)
  return stored === 'true'
}

export const Layout = () => {
  const [isNavigationOpen, setIsNavigationOpen] = useState(getInitialNavState)
  const [isQuickSearch, setIsQuickSearch] = useState(false)

  // Persist nav open state to localStorage
  const handleNavigationOpenChange = useCallback((isOpen) => {
    setIsNavigationOpen(isOpen)
    localStorage.setItem(NAV_OPEN_STORAGE_KEY, String(isOpen))
  }, [])
  const { data, isLoading } = useMe()
  // Lazy load profile image to avoid blocking initial page load (~1400ms signed URL)
  const { data: profileImageData } = useMeProfileImage({ enabled: !!data?.me?.id })
  const profileImageUrl = profileImageData?.me?.profileImage?.url
  const { hasPermission, hasAnyPermission, getAccessibleSites, hasUniversalSiteAccess } =
    usePermissions()
  const hasDownloadReportsPermission = hasPermission('download_exports')
  const hasReadControls = hasPermission('read_controls')
  const { hasBrandControls } = useBrandReportsAccess()
  const hasBrandContentRead = hasBrandControls || hasPermission('content__read')
  const { value: isAssistantSidenavEnabled } = useFeatureFlag(
    FeatureFlagKeys.AssistantSidenavEnabled,
    { defaultValue: false }
  )
  const { value: isContentSuiteEnabled } = useFeatureFlag(FeatureFlagKeys.ContentSuiteEnabled, {
    defaultValue: false,
  })
  const { value: isSelfServiceEnabled } = useFeatureFlag(FeatureFlagKeys.SelfServiceEnabled, {
    defaultValue: false,
  })
  const { value: isCapitalEnabled } = useFeatureFlag(FeatureFlagKeys.CapitalEnabled, {
    defaultValue: false,
  })
  const { value: isClassCoversEnabled } = useFeatureFlag(FeatureFlagKeys.ClassCoversEnabled, {
    defaultValue: false,
  })

  // Navigation permission checks
  // Show Schedule menu if user has ANY schedule permission — not just the 3 operational ones.
  // Previously only checked 3 of 17, so trainers with e.g. only SOAP notes or cancel permissions
  // would lose the entire Schedule menu item.
  const hasScheduleAccess = hasAnyPermission(getAllPermissionKeys(SCHEDULE_PERMISSIONS))
  const hasClientsAccess = hasAnyPermission(['access__clients', 'clients__client_merge'])
  const hasCoversAccess = hasAnyPermission(COVER_BOARD_PERMISSIONS)
  const hasReportsAccess = hasAnyPermission([
    'reports__client_and_attendance',
    'reports__staff',
    'reports__inventory',
    'reports__financial',
    'access__reports',
  ])
  const hasSettingsAccess = hasAnyPermission([
    'access__settings',
    'settings__location',
    'settings__retail',
    'settings__account',
    'settings__scheduler',
  ])
  const hasPaymentsAccess = hasAnyPermission([
    'payments__request_payment_access',
    'payments__process_payments_within_schedule',
    'payments__access_to_cash_draw',
  ])
  const hasAnalyticsAccess = hasPermission('bi_view_only')
  const location = useLocation()
  const navigate = useNavigate()
  const { isLoading: isLoadingAuth, hasValidSession } = useAuth()
  const { clearCurrentSite, currentSite, isCheckInEnabled, setIsCheckInEnabled } = useSiteStore()
  // Gate site-level queries on hasValidSession to prevent 401s on the login page.
  // currentSite persists in Zustand localStorage, so siteId can be non-null even when logged out.
  const authenticatedSiteId = hasValidSession ? currentSite?.siteId : undefined
  const { data: paymentGatewayOnboardingStatus } =
    usePaymentGatewayOnboardingStatus(authenticatedSiteId)
  const { navigateToSite, navigateToCorporate, buildSitePath, buildCorporatePath } =
    useSiteNavigation()

  // Capital only exists for Stripe-backed locations, so the nav item needs the gateway answer as
  // well as the flag and permission. The lookup is skipped entirely while the flag is off.
  const { isAvailable: hasCapitalAccess } = useCapitalAccess(authenticatedSiteId)

  const { data: siteData } = useGetSiteById(authenticatedSiteId, { cacheTime: 0, staleTime: 0 })
  const siteConfigurations = siteData?.settings?.location?.configurations

  useEffect(() => {
    if (!currentSite?.siteId) {
      setIsCheckInEnabled(false)
      return
    }
    const enabled = siteData?.settings?.location?.checkIn?.enableCheckInWidget
    setIsCheckInEnabled(!!enabled)
  }, [currentSite?.siteId, siteData?.settings?.location?.checkIn?.enableCheckInWidget])
  if (siteConfigurations?.dateFormat) {
    localStorage.setItem(SITE_DATE_FORMAT_KEY, siteConfigurations.dateFormat)
  }
  if (siteConfigurations?.timeFormat) {
    localStorage.setItem(SITE_TIME_FORMAT_KEY, siteConfigurations.timeFormat)
  }
  if (siteConfigurations?.numberFormat) {
    localStorage.setItem(SITE_NUMBER_FORMAT_KEY, siteConfigurations.numberFormat)
  }
  const { isImpersonating, impersonationInfo } = useImpersonationSession()

  const autoLogoutOperations = siteData?.settings?.location?.operations

  // Onboarding panel integration
  const { onboardingHidden } = useOnboardingStore()
  const {
    isOpen: isPanelOpen,
    setIsOpen: setPanelOpen,
    activePanel,
    setActivePanel,
  } = useSupportAssistant()
  const { corporate: corporateData } = useCorporateWithStore()
  const trialCorporateId = corporateData?.corporateBySubdomain?.corporateId
  const { data: trialData } = useTrialStatus(trialCorporateId)
  const isTrialUser =
    trialData?.trialStatus && ['trial_active', 'trial_expiring'].includes(trialData.trialStatus)
  const trialDaysRemaining = trialData?.daysRemaining ?? null
  const [showHideOnboardingDialog, setShowHideOnboardingDialog] = useState(false)

  // Auto-open onboarding sidebar for trial users on login (once per session)
  useEffect(() => {
    if (isTrialUser && !onboardingHidden) {
      const key = 'onboarding-auto-opened'
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        setActivePanel('onboarding')
        setPanelOpen(true)
      }
    }
  }, [isTrialUser, onboardingHidden, setActivePanel, setPanelOpen])

  useKBarShortcuts()

  // Track page views for PostHog analytics (including initial page load)
  const prevPathRef = useRef(null)
  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname
      // Strip UUIDs from path for cleaner grouping (e.g. /s/abc-123/clients → /s/:siteId/clients)
      const cleanPath = location.pathname.replace(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
        ':id'
      )
      trackEvent('page.viewed', {
        page_path: cleanPath,
        page_path_raw: location.pathname,
        page_title: document.title,
      })
    }
  }, [location.pathname])

  // Clear currentSite when navigating to corporate routes
  useEffect(() => {
    const isCorporateRoute = location.pathname.startsWith('/c/')
    if (isCorporateRoute && currentSite) {
      clearCurrentSite()
    }
  }, [location.pathname, currentSite, clearCurrentSite])

  const pathSegment = usePathSegment()
  const dynamicLabels = useBreadcrumbStore((state) => state.dynamicLabels)

  // If the path segment is a UUID (siteId), replace with the actual site name for breadcrumbs
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    pathSegment || ''
  )
  const resolvedPathSegment = isUUID ? currentSite?.name || pathSegment : pathSegment

  const dynamicBreadcrumbObject = {
    tab: resolvedPathSegment,
    ...dynamicLabels,
  }

  const breadcrumbs = useBreadcrumbs(PROTECTED_ROUTES_PATHS, location, dynamicBreadcrumbObject)

  const tabsArray = useMemo(() => {
    if (resolvedPathSegment) {
      return [
        {
          id: resolvedPathSegment.toString(),
          label: resolvedPathSegment,
        },
      ]
    }
    return []
  }, [resolvedPathSegment])

  const shouldShowBreadcrumbs = useMemo(() => {
    const pathToCheck = location.pathname

    // Strip site/corporate prefix for route matching (e.g., /s/{siteId}/clients -> /clients)
    const pathWithoutPrefix = pathToCheck.replace(/^\/(s\/[^/]+|c)/, '')

    return !ROUTES_WITHOUT_BREADCRUMBS.some(
      (route) =>
        pathToCheck === route ||
        pathToCheck.startsWith(route) ||
        pathWithoutPrefix === route ||
        pathWithoutPrefix.startsWith(route)
    )
  }, [location.pathname])

  const shouldShowNavigation = useMemo(() => {
    const pathToCheck = location.pathname
    return pathToCheck !== ROUTES.locationSelection.path
  }, [location.pathname])

  const isSettingsRoute = useMemo(() => {
    return SETTINGS_ROUTES.some((route) => location.pathname.startsWith(route))
  }, [location.pathname])

  // Permission map for nav items
  const navPermissionMap = useMemo(
    () => ({
      schedule: hasScheduleAccess,
      clients: hasClientsAccess,
      reports: hasReportsAccess,
      analytics: hasAnalyticsAccess,
      settings: hasSettingsAccess,
      payments: hasPaymentsAccess,
      capital: hasCapitalAccess,
      covers: hasCoversAccess,
      'control-hub': currentSite ? undefined : hasBrandControls,
      contentSuite: currentSite ? undefined : hasBrandContentRead,
    }),
    [
      hasScheduleAccess,
      hasClientsAccess,
      hasReportsAccess,
      hasAnalyticsAccess,
      hasSettingsAccess,
      hasPaymentsAccess,
      hasCapitalAccess,
      hasCoversAccess,
      currentSite,
      hasBrandControls,
      hasBrandContentRead,
    ]
  )

  // OC-320: lock nav items whose feature the current site's plan tier does not include. Fails open
  // (hasFeature is true while entitlements load or when tiering is off), so nav is unchanged until a
  // brand is deliberately switched on. `isEntitlementsLoading` extends the initial-load skeleton below
  // to also wait for BRAND entitlements, so the nav and brand-scoped screens never flash ungated
  // before the gate resolves (site-scoped screens wait via their own SiteEntitlementsProvider).
  const { hasFeature, checkFeature, isLoading: isEntitlementsLoading } = useCoreEntitlements()

  // Feature flag map for nav items that require a PostHog flag to be shown
  const featureFlagMap = useMemo(
    () => ({
      [FeatureFlagKeys.AssistantSidenavEnabled]: isAssistantSidenavEnabled,
      [FeatureFlagKeys.ContentSuiteEnabled]: isContentSuiteEnabled,
      [FeatureFlagKeys.SelfServiceEnabled]: isSelfServiceEnabled,
      [FeatureFlagKeys.CapitalEnabled]: isCapitalEnabled,
      [FeatureFlagKeys.ClassCoversEnabled]: isClassCoversEnabled,
    }),
    [
      isAssistantSidenavEnabled,
      isContentSuiteEnabled,
      isSelfServiceEnabled,
      isCapitalEnabled,
      isClassCoversEnabled,
    ]
  )

  const currentNavItems = useMemo(() => {
    if (isSettingsRoute) {
      const settingsItems = {
        withLocation: {
          main: Object.values(settingsNavItems)
            .filter((item) => item.key !== 'userProfile')
            .map((item) => ({
              ...item,
              label: item.name,
              href: item.to,
              id: item.key,
            })),
        },
        userProfile: {
          ...settingsNavItems.userProfile,
          label: settingsNavItems.userProfile.name,
          src: profileImageUrl,
        },
        teamAccess: navItems.teamAccess,
        bottomDefault: SETTINGS_BOTTOM_NAVIGATIONS.withLocation.bottomDefault,
      }

      return settingsItems
    } else {
      const baseNavItems = currentSite ? locationNavItems : navItems

      const filterNavItem = (item) => {
        if (!currentSite && item.requiresSiteId) {
          return false
        }
        if (item.featureFlag && !featureFlagMap[item.featureFlag]) {
          return false
        }
        const permissionCheck = navPermissionMap[item.id]
        if (permissionCheck !== undefined && !permissionCheck) {
          return false
        }
        return true
      }

      // OC-320: a nav item whose feature the plan tier does not include stays VISIBLE but is NOT
      // clickable — it carries a lock icon and, on hover, the upgrade message (consistent with the
      // Control Center cards and every other gated affordance); only direct route navigation shows
      // the feature-disabled page. Fails open — hasFeature is true while entitlements load or while
      // tiering is off, so nav is unchanged until a brand is deliberately switched on.
      const applyEntitlementLock = (item) =>
        item.entitlement && !hasFeature(item.entitlement)
          ? { ...item, locked: true, lockedCheckResult: checkFeature(item.entitlement) }
          : item

      // Build onboarding nav item if user is on trial and hasn't hidden it
      const onboardingNavItem =
        isTrialUser && !onboardingHidden
          ? {
              icon: IconClipboardCheck,
              id: 'onboarding',
              label: 'Onboarding',
              subtitle:
                trialDaysRemaining !== null
                  ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left`
                  : undefined,
              menuActions: [
                {
                  id: 'hide-onboarding',
                  label: 'Hide onboarding',
                  onClick: () => setShowHideOnboardingDialog(true),
                },
              ],
            }
          : null

      const mainBottomItems = onboardingNavItem ? [onboardingNavItem] : undefined

      const regularNavItems = {
        ...baseNavItems,
        withLocation: {
          ...baseNavItems.withLocation,
          main: baseNavItems.withLocation.main.filter(filterNavItem).map(applyEntitlementLock),
          mainBottom: mainBottomItems,
        },
        ...(baseNavItems.withoutLocation && {
          withoutLocation: {
            ...baseNavItems.withoutLocation,
            main: baseNavItems.withoutLocation.main.filter(filterNavItem).map(applyEntitlementLock),
            mainBottom: mainBottomItems,
          },
        }),
        teamAccess: navItems.teamAccess,
        userProfile: {
          ...navItems.userProfile,
          src: profileImageUrl,
        },
      }

      return regularNavItems
    }
  }, [
    isSettingsRoute,
    profileImageUrl,
    currentSite,
    navPermissionMap,
    featureFlagMap,
    hasFeature,
    isTrialUser,
    onboardingHidden,
    trialDaysRemaining,
  ])

  const currentBottomNavItems = useMemo(() => {
    if (isSettingsRoute) {
      return SETTINGS_BOTTOM_NAVIGATIONS
    }

    // Check if user has any site access (from permissions check in layout)
    const hasUserData = !!data?.me
    const accessibleSiteIds = hasUserData ? getAccessibleSites() : []
    const hasUniversalAccess = hasUserData ? hasUniversalSiteAccess() : false
    const hasSiteAccess = hasUniversalAccess || accessibleSiteIds.length > 0

    // Add location selector to bottom navigation items - show if user has site access
    const locItems =
      hasSiteAccess && !isSettingsRoute
        ? [
            {
              id: 'location-selector',
              label: currentSite?.name || 'Go to location',
              icon: IconMapMarker,
            },
          ]
        : undefined

    // Build extra mobile menu items (onboarding + assistant)
    const extraMobileItems = []
    if (isTrialUser && !onboardingHidden) {
      extraMobileItems.push({
        icon: IconClipboardCheck,
        id: 'onboarding',
        label: 'Onboarding',
      })
    }
    if (isAssistantSidenavEnabled) {
      extraMobileItems.push({
        icon: IconAssistant,
        id: 'assistant',
        label: 'Assistant',
      })
    }

    // When a site is selected, use locationNavItems for bottom navigation
    if (currentSite) {
      const filteredMain = locationNavItems.withLocation.main.filter((item) => {
        // Apply permission filtering
        const permissionCheck = navPermissionMap[item.id]
        if (permissionCheck !== undefined && !permissionCheck) {
          return false
        }
        // Skip assistant from locationNavItems — we add it via extraMobileItems with panel toggle
        if (item.id === 'assistant') {
          return false
        }
        return true
      })

      return {
        loc: locItems,
        withLocation: {
          ...BOTTOM_NAVIGATIONS.withLocation,
          main: [...filteredMain, ...extraMobileItems],
        },
        withoutLocation: BOTTOM_NAVIGATIONS.withoutLocation,
      }
    }

    // Add loc to bottom navigation even when no site is selected (for "Go to location")
    // Also add extra mobile items to the withoutLocation and withLocation main lists
    return {
      ...BOTTOM_NAVIGATIONS,
      loc: locItems,
      withoutLocation: {
        ...BOTTOM_NAVIGATIONS.withoutLocation,
        main: [...(BOTTOM_NAVIGATIONS.withoutLocation.main || []), ...extraMobileItems],
      },
      withLocation: {
        ...BOTTOM_NAVIGATIONS.withLocation,
        main: [...(BOTTOM_NAVIGATIONS.withLocation.main || []), ...extraMobileItems],
      },
    }
  }, [
    isSettingsRoute,
    currentSite,
    navPermissionMap,
    data?.me,
    getAccessibleSites,
    hasUniversalSiteAccess,
    isTrialUser,
    onboardingHidden,
    isAssistantSidenavEnabled,
  ])

  const resolveNavigationPath = useCallback(
    (path) => {
      // Handle paths that already have /c/ or /s/ prefix
      if (path.startsWith('/c/') || path.startsWith('/s/')) {
        return path
      }

      // Define site-level routes that require siteId in path
      const siteSpecificRoutes = [
        '/schedule',
        '/clients',
        '/reports',
        '/retail',
        '/payments',
        '/team-access',
      ]

      // Define corporate-level routes that use /c/* pattern
      const corporateRoutes = ['/user-access', '/control-center']

      // Settings can be either site-level or corporate-level
      // If user has a currentSite, treat /settings as site-level
      // Otherwise, treat it as corporate-level
      const isSiteSettings = path === '/settings' && currentSite

      // Check if target path is site-specific
      const isTargetSiteSpecific =
        path === '/' ||
        isSiteSettings ||
        siteSpecificRoutes.some((route) => path === route || path.startsWith(route + '/'))

      // Check if target path is corporate-specific
      const isTargetCorporate = corporateRoutes.some(
        (route) => path === route || path.startsWith(route + '/')
      )

      // Route appropriately
      if (isTargetSiteSpecific && currentSite) {
        return buildSitePath(path)
      }
      if (isTargetCorporate) {
        return buildCorporatePath(path)
      }
      if (path === '/location-selection') {
        return path
      }
      return currentSite ? buildSitePath(path) : buildCorporatePath(path)
    },
    [currentSite, buildSitePath, buildCorporatePath]
  )

  const handleNavigation = useCallback(
    (path) => navigate(resolveNavigationPath(path)),
    [navigate, resolveNavigationPath]
  )

  // Handle clicks on special nav items (onboarding, assistant) that toggle the right panel
  const handleNavItemClick = useCallback(
    (item) => {
      if (item.id === 'onboarding') {
        if (isPanelOpen && activePanel === 'onboarding') {
          setPanelOpen(false)
        } else {
          setActivePanel('onboarding')
          setPanelOpen(true)
        }
        return true // skip default routing
      }
      if (item.id === 'assistant') {
        if (isPanelOpen && activePanel === 'assistant') {
          setPanelOpen(false)
        } else {
          setActivePanel('assistant')
          setPanelOpen(true)
        }
        return true // skip default routing
      }
      if (item.id === 'member-view') {
        // Close both sidebars, then navigate within the current context
        // (location-level stays at /s/:siteId/member-view, brand-level at /c/member-view)
        setPanelOpen(false)
        handleNavigationOpenChange(false)
        if (currentSite?.siteId) {
          navigateToSite('member-view')
        } else {
          navigateToCorporate('member-view')
        }
        return true // skip default routing
      }
      return false // use default routing
    },
    [
      isPanelOpen,
      activePanel,
      setPanelOpen,
      setActivePanel,
      handleNavigationOpenChange,
      navigateToCorporate,
      navigateToSite,
      currentSite?.siteId,
    ]
  )

  useLoginAsSync()

  const isSandbox = window.location.hostname.includes('sandbox')

  // Measure banner container height and share via Zustand so the left nav,
  // main content, and right sidebar can each add internal top padding.
  const bannerContainerRef = useRef(null)
  const topBannerHeight = useBannerStore((s) => s.topBannerHeight)
  const setTopBannerHeight = useBannerStore((s) => s.setTopBannerHeight)
  useEffect(() => {
    const el = bannerContainerRef.current
    if (!el) {
      setTopBannerHeight(0)
      return
    }

    const update = () => {
      setTopBannerHeight(el.offsetHeight || 0)
    }
    update()

    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      observer.disconnect()
      setTopBannerHeight(0)
    }
  }, [
    isSandbox,
    isImpersonating,
    impersonationInfo,
    isLoadingAuth,
    isLoading,
    isEntitlementsLoading,
    setTopBannerHeight,
  ])

  // Show nothing while auth or user data is loading — prevents flash of skeleton/nav.
  // Exception: never hide on the login page — the login form must stay visible during sign-in.
  const isLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login'
  // Also wait for BRAND entitlements so the nav and brand-scoped screens paint correctly-gated in one
  // go — no ungated flash before the lock/wall resolves. Entitlements fail open on error (isLoading
  // resolves), so this can never get stuck. Site-scoped screens wait via SiteEntitlementsProvider.
  if (!isLoginPage && (isLoadingAuth || isLoading || isEntitlementsLoading)) {
    return hasStoredCoreSession() ? <LayoutSkeleton /> : null
  }

  return (
    <Box sx={styles.wrapper}>
      <IdleSessionGuard
        siteId={currentSite?.siteId}
        locationAutoLogout={autoLogoutOperations?.autoLogout ?? false}
        locationAutoLogoutMinutes={autoLogoutOperations?.autoLogoutMinutes ?? 'FIVE'}
        userType={data?.me?.type}
        isImpersonating={isImpersonating}
      />
      <Box ref={bannerContainerRef} sx={styles.bannerContainer}>
        <UnsavedChangesBanner />
        {isSandbox && <SandboxBanner />}
        {(paymentGatewayOnboardingStatus?.requiresAttention ||
          paymentGatewayOnboardingStatus?.hasEventuallyDueRequirements) && (
          <PaymentGatewayBanner
            siteId={currentSite?.siteId}
            onboardingStatus={paymentGatewayOnboardingStatus.onboardingStatus}
            hasEventuallyDueRequirements={
              paymentGatewayOnboardingStatus.hasEventuallyDueRequirements
            }
          />
        )}
        {isImpersonating && impersonationInfo && (
          <ImpersonationBanner
            staffUser={impersonationInfo.staffUser}
            adminUser={impersonationInfo.adminUser}
          />
        )}
      </Box>
      <Box sx={styles.root}>
        {hasValidSession ? (
          <>
            {shouldShowNavigation && (
              <Navigation
                setRoute={handleNavigation}
                navItems={currentNavItems}
                isOpen={isNavigationOpen}
                hasRouteIntegration
                isLocation={!!currentSite}
                onOpen={handleNavigationOpenChange}
                pathname={location.pathname}
                bottomNavItems={currentBottomNavItems}
                setisQuickSearch={setIsQuickSearch}
                header={<NavigationHeader />}
                authState={{ isLoadingAuth, hasValidSession }}
                userData={{ data, isLoading }}
                topBannerHeight={topBannerHeight}
                onItemClick={handleNavItemClick}
                resolveHref={resolveNavigationPath}
              />
            )}
            <Box
              sx={
                shouldShowNavigation
                  ? styles.main(topBannerHeight)
                  : styles.fullWidth(topBannerHeight)
              }
            >
              {shouldShowBreadcrumbs && hasValidSession && (
                <Box sx={styles.breadcrumbs}>
                  <CoreBreadCrumbs
                    breadcrumbs={breadcrumbs}
                    pathname={location.pathname}
                    tabs={tabsArray}
                    params={{}}
                    entityType={BRAND_NAME}
                    hasDownloadReportsPermission={hasDownloadReportsPermission}
                    hasReadControls={hasReadControls}
                  />
                </Box>
              )}

              <OnboardingWrapper>
                <Outlet context={isQuickSearch} />
              </OnboardingWrapper>
            </Box>
            {isCheckInEnabled && <CheckInFloatingButton />}
            <HideOnboardingDialog
              open={showHideOnboardingDialog}
              onClose={() => setShowHideOnboardingDialog(false)}
            />
          </>
        ) : (
          <>
            {shouldShowBreadcrumbs && hasValidSession && (
              <Box sx={styles.breadcrumbs}>
                <CoreBreadCrumbs
                  breadcrumbs={breadcrumbs}
                  pathname={location.pathname}
                  tabs={tabsArray}
                  params={{}}
                  entityType={BRAND_NAME}
                  hasDownloadReportsPermission={hasDownloadReportsPermission}
                  hasReadControls={hasReadControls}
                />
              </Box>
            )}

            <Outlet context={isQuickSearch} />
          </>
        )}
      </Box>
    </Box>
  )
}

export default Layout
